<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ClickLight from '@/components/svg/ClickLight.vue';
import type { AvailableLocales, LocaleSchema } from '@/locales/index.ts';

const { t } = useI18n<{message: LocaleSchema}, AvailableLocales>({
  useScope: 'global',
});

const props = withDefaults(defineProps<{
  messageKey: string;
  visible: boolean;
  toClickElement?: HTMLElement;
  position?: 'top' | 'bottom' | 'left' | 'right';
  autoHide?: boolean;
  storageKey?: string;
  alwaysShow?: boolean;
}>(), {
  position: 'top',
  autoHide: false,
  alwaysShow: true,
});

const emit = defineEmits<{
  'dismiss': [];
  'shown': [];
  'hidden': [];
}>();

const tooltipRef = ref<HTMLDivElement>();
const isVisible = ref(false);
const shouldShow = ref(false);
let hideTimeout: number | undefined;

function hasBeenSeen() {
  if (!props.storageKey) return false;
  return localStorage.getItem(props.storageKey) === 'true';
}

function markAsSeen() {
  if (props.storageKey) {
    localStorage.setItem(props.storageKey, 'true');
  }
}

async function show() {
  if (hasBeenSeen() && !props.alwaysShow) return;
  
  shouldShow.value = true;
  await nextTick();
  isVisible.value = true;
  emit('shown');
  
  if (props.autoHide) {
    hideTimeout = setTimeout(() => {
      hide();
    }, 10000);
  }
}

function hide() {
  isVisible.value = false;
  shouldShow.value = false;
  markAsSeen();
  emit('hidden');
  
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = undefined;
  }
}

function dismiss() {
  hide();
  emit('dismiss');
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isVisible.value) {
    dismiss();
  }
}

function handleElementClick(event: Event) {
  if (isVisible.value) {
    const target = event.target as HTMLElement;
    if (target.closest('.renderCanvas-polygonShape') || target.closest('svg')) {
      dismiss();
    }
  }
}

function removeElementListener(element?: HTMLElement) {
  if (element) {
    element.removeEventListener('click', handleElementClick);
  }
}

function addElementListener(element?: HTMLElement) {
  if (element) {
    element.addEventListener('click', handleElementClick);
  }
}

watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    show();
  } else {
    hide();
  }
});

watch(() => props.toClickElement, (newElement, oldElement) => {
  if( oldElement) {
    removeElementListener(oldElement);
  }

  if (newElement) {
    addElementListener(newElement);
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  
  if (hideTimeout) {
    clearTimeout(hideTimeout);
  }
});
</script>

<template>
  <Transition name="onboardingFade">
    <div v-if="shouldShow && isVisible"
         ref="tooltipRef"
         class="onboardingTooltip"
         :class="`onboardingTooltip_${position}`"
         role="tooltip"
         :aria-label="t(messageKey)">
      <div class="onboardingTooltip-content">
        <div class="onboardingTooltip-icon">
          <ClickLight />
        </div>
        <div class="onboardingTooltip-message">
          {{ t(messageKey) }}
        </div>
        <button class="onboardingTooltip-close"
                type="button"
                :aria-label="t('common.close')"
                @click="dismiss">
          ×
        </button>
      </div>
      <div class="onboardingTooltip-arrow"></div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.onboardingTooltip {
  position: absolute;
  z-index: 99;
  pointer-events: auto;
  
  &_top {
    top: -10px;
    left: 50%;
    translate: -50% 0;
  }
  
  &_bottom {
    bottom: 20px;
    left: 50%;
    translate: -50% 0;
  }
  
  &_left {
    left: 20px;
    top: 50%;
    translate: 0 -50%;
  }
  
  &_right {
    right: 20px;
    top: 50%;
    translate: 0 -50%;
  }
}

.onboardingTooltip-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  min-width: 280px;
  max-width: 400px;
  border-radius: 6px;
  
  background-color: var(--gray-950, #000);
  color: var(--white, #fff);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.onboardingTooltip-icon {
  --iconSize: 32px;
  flex-shrink: 0;
  width: var(--iconSize);
  height: var(--iconSize);
  
  :deep(svg) {
    width: 100%;
    height: 100%;
    
    .click-hand {
      fill: var(--white, #fff);
    }
    
    .click-effect {
      fill: var(--white, #fff);
      opacity: 0.8;
    }
  }
}

.onboardingTooltip-message {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
}

.onboardingTooltip-close {
  background: none;
  border: none;
  color: var(--white, #fff);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: 2px solid var(--white, #fff);
    outline-offset: 2px;
  }
}

.onboardingTooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  
  .onboardingTooltip_top & {
    bottom: -8px;
    left: 50%;
    translate: -50% 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid var(--gray-950, #000);
  }
  
  .onboardingTooltip_bottom & {
    top: -8px;
    left: 50%;
    translate: -50% 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--gray-950, #000);
  }
  
  .onboardingTooltip_left & {
    right: -8px;
    top: 50%;
    translate: 0 -50%;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid var(--gray-950, #000);
  }
  
  .onboardingTooltip_right & {
    left: -8px;
    top: 50%;
    translate: 0 -50%;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid var(--gray-950, #000);
  }
}

.onboardingFade-enter-active {
  transition: all 0.3s ease-out;
}

.onboardingFade-leave-active {
  transition: all 0.2s ease-in;
}

.onboardingFade-enter-from {
  opacity: 0;
  scale: 0.95;
  
  .onboardingTooltip_top & {
    translate: -50% -10px;
  }
  
  .onboardingTooltip_bottom & {
    translate: -50% 10px;
  }
  
  .onboardingTooltip_left & {
    translate: -10px -50%;
  }
  
  .onboardingTooltip_right & {
    translate: 10px -50%;
  }
}

.onboardingFade-leave-to {
  opacity: 0;
  scale: 0.95;
}
</style>
